import React, { useState } from 'react';
import { Stack, Chip, Tooltip, Typography, Button, Popover, Box } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';

export const TableFilterChips = ({ activeFilter, onFilterClick, items = [], title = "Estatus:", disabled = false, isCompact = false, compactLabel = null }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  if (!items || items.length === 0) return null;

  const activeItem = items.find(it => (it.value !== undefined ? it.value : it.label) === activeFilter);

  // Modo compacto (Menú desplegable en 1 solo botón)
  if (isCompact) {
    return (
      <>
        <Button
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          disabled={disabled}
          startIcon={<FilterListIcon sx={{ fontSize: 13 }} />}
          sx={{
            height: 28,
            px: 1,
            fontSize: '0.68rem',
            fontWeight: 700,
            borderRadius: '8px',
            bgcolor: activeItem ? activeItem.bg : '#ffffff',
            color: activeItem ? activeItem.color : '#475569',
            border: '1px solid #cbd5e1',
            textTransform: 'none',
            boxShadow: 'none',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            '&:hover': { bgcolor: activeItem ? activeItem.bg : '#f8fafc', borderColor: '#94a3b8' },
          }}
        >
          {activeItem ? `Estatus: ${activeItem.label}` : (compactLabel || 'Estatus (Todos)')}
        </Button>
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { p: 1.2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' } }}
        >
          <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', display: 'block', mb: 1 }}>
            Filtrar por Estatus:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, maxWidth: 260 }}>
            {items.map((it) => {
              const value = it.value !== undefined ? it.value : it.label;
              const isActive = activeFilter === value;
              return (
                <Chip
                  key={value}
                  label={it.label}
                  size="small"
                  onClick={() => {
                    if (onFilterClick) onFilterClick(isActive ? '' : value);
                    setAnchorEl(null);
                  }}
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    bgcolor: it.bg,
                    color: it.color,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    border: isActive ? '2px solid #0f172a' : '1px solid transparent',
                  }}
                />
              );
            })}
          </Box>
        </Popover>
      </>
    );
  }

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{
        whiteSpace: 'nowrap',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        transition: 'opacity 0.2s',
        overflowX: 'auto',
        maxWidth: '100%',
        py: 0.2,
        '&::-webkit-scrollbar': { height: 2 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.15)', borderRadius: 2 },
      }}
    >
      {title && (
        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.68rem', color: '#64748b', mr: 0.25 }}>
          {title}
        </Typography>
      )}

      {items.map((it) => {
        const value = it.value !== undefined ? it.value : it.label;
        const isActive = activeFilter === value;
        const chipEl = (
          <Chip
            label={it.label}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (onFilterClick) onFilterClick(isActive ? '' : value);
            }}
            sx={{
              height: 18,
              fontSize: '0.65rem',
              fontWeight: 700,
              bgcolor: it.bg,
              color: it.color,
              borderRadius: '4px',
              border: '1px solid transparent',
              '& .MuiChip-label': { px: 0.6 },
              cursor: 'pointer',
              transition: 'all 0.15s',
              ...(activeFilter
                ? {
                    opacity: isActive ? 1 : 0.4,
                    transform: isActive ? 'scale(1.05)' : 'scale(1)',
                    boxShadow: isActive ? '0px 2px 4px rgba(0,0,0,0.2)' : 'none',
                  }
                : {
                    opacity: 1,
                    '&:hover': {
                      opacity: 0.8,
                    }
                  }),
            }}
          />
        );

        if (!it.help) {
          return <React.Fragment key={value}>{chipEl}</React.Fragment>;
        }

        return (
          <Tooltip key={value} title={it.help} arrow placement="top">
            <span>{chipEl}</span>
          </Tooltip>
        );
      })}
    </Stack>
  );
};
